<template>
  <v-dialog v-model="loginActive" width="500" persistent>
    <v-card>
      <v-card-title primary-title>
        <img :src="require('@/assets/shield.svg')" style="margin-right:1em;" contain height="50" alt="Covenant Shield Logo" />
        Scots Connect Login
      </v-card-title>
      <v-tabs v-model="tab" style="padding-top:16px" light grow>
        <v-tab>Student Login</v-tab>
        <v-tab>Family Login</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-card-text>
            <div style="min-height:2em">
              <v-alert v-if="errorMessage" type="error" dense>{{ errorMessage }}</v-alert>
              <v-alert v-for="(message, index) in messages" :key="'message-' + index" type="info" dense>{{ message }}</v-alert>
            </div>
            <v-text-field v-model="username" :label="loginType === 'ldap' ? 'Covenant Username' : 'Email'" ref="usernameField" required outlined @focus="resetTimeout" @keyup.enter="checkSubmit()"></v-text-field>
            <v-text-field v-model="password" label="Password" ref="passwordField" type="password" required outlined hide-details @focus="resetTimeout" @keyup.enter="checkSubmit()"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" size="medium" @click="checkSubmit()" :disabled="checkingLogin">{{ buttonMessage }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn to="/setup">Set Up Student Account</v-btn>
          </v-card-actions>
        </v-tab-item>
        <v-tab-item>
          <v-card-text>
            <div style="min-height:2em">
              <v-alert v-if="errorMessage" type="error" dense>{{ errorMessage }}</v-alert>
              <v-alert v-for="(message, index) in messages" :key="'message-' + index" type="info" dense>{{ message }}</v-alert>
            </div>
            <v-text-field v-model="username" label="Email" ref="usernameField" required outlined @focus="resetTimeout" @keyup.enter="checkSubmit()"></v-text-field>
            <v-text-field v-model="password" label="Password" ref="passwordField" type="password" required outlined hide-details @focus="resetTimeout" @keyup.enter="checkSubmit()"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" size="medium" @click="checkSubmit()" :disabled="checkingLogin">{{ buttonMessage }}</v-btn>
            <v-btn to="/profile/reset" outlined>Forgot Password</v-btn>
            <v-spacer></v-spacer>
            <v-btn to="/profile">Create an account</v-btn>
          </v-card-actions>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref, reactive, computed, watch } from '@vue/composition-api'

export default {
  props: {
    messages: Array
  },
  setup (props, { root }) {
    const tab = ref(0)
    const username = ref('')
    const password = ref('')
    const usernameField = ref(null)
    const passwordField = ref(null)
    const errorMessage = ref(null)
    const checkingLogin = ref(false)
    const userTemp = reactive({})

    const loginActive = computed(() => !root.$store.state.loggedIn)
    const buttonMessage = computed(() => checkingLogin.value ? 'Logging in...' : 'Log In')
    const user = computed(() => root.$feathers.get('user'))

    function checkSubmit () {
      if (username.value === '') usernameField.value.focus()
      else if (password.value === '') passwordField.value.focus()
      else doLogin()
    }

    function doLogin () {
      checkingLogin.value = true
      root.$feathers.authenticate({
        strategy: tab.value === 0 ? 'ldap' : 'local',
        username: username.value.toLowerCase(),
        password: password.value,
        special: 'alternative'
      }).then(async (response) => {
        checkingLogin.value = false
        if ('user' in response) {
          root.$feathers.set('user', response.user)
          if ('roles' in response.user) {
            let isStudent = false
            for (let i = 0; i < response.user.roles.length; i++) {
              if (response.user.roles[i] === 'Student') isStudent = true
            }
            // Verify the term the student is in
            if (isStudent) {
              const { data: [{ term }] } = await root.$feathers.service('orientation/tracker').find({ query: { pidm: response.user.pidm } })
              const { data } = await root.$feathers.service('orientation/term').find({ query: { term } })
              if (data.length > 0) {
                const { openDate } = data[0]
                let dt = new Date(openDate)
                let now = new Date()
                if (dt > now) {
                  const dateString = dt.toLocaleString().replace(',', ' at').replace(':00:00', ':00')
                  errorMessage.value = 'Scots Connect will open on ' + dateString + '. Please try again after this time.'
                  root.$feathers.logout()
                  return
                }
              } else {
                errorMessage.value = 'There was an error loading your information. Please try again later or contact your Admissions Counselor for assistance.'
                root.$feathers.logout()
                return
              }
            }
          } else {
            errorMessage.value = 'An unknown error has occurred. Please try again. If the problem persists, please contact Technology Services.'
            checkingLogin.value = false
            return
          }
          root.$store.dispatch('login', response.user)
          root.$emit('loginSuccess')
          errorMessage.value = null
          password.value = ''
        } else {
          errorMessage.value = 'An unknown error has occurred. Please try again. If the problem persists, please contact Technology Services.'
          checkingLogin.value = false
        }
      }).catch(e => {
        if (e.message === 'Cannot read property \'eventMappings\' of undefined') errorMessage.value = 'Invalid username or password; logon denied'
        else errorMessage.value = e.message
        checkingLogin.value = false
      })
    }

    const semiTransparent = ref(false)
    const timeout = ref(null)

    watch(loginActive, () => {
      if (loginActive.value === true) {
        resetTimeout()
      } else {
        timeout.value = null
        semiTransparent.value = false
      }
    })

    function hasClass (el, className) {
      let arr = el.classList
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] === className) return true
      }
      return false
    }

    function resetTimeout () {
      semiTransparent.value = false
      clearTimeout(timeout.value)
      timeout.value = setTimeout(() => {
        let el = document.activeElement
        if (el != null) {
          while (document.activeElement.tagName !== 'BODY' && el != null) {
            if (hasClass(el, 'v-card')) {
              resetTimeout()
              return
            }
            el = el.parentElement
          }
        }
        semiTransparent.value = true
      }, 5000)
    }

    const loginType = ref('ldap')
    const loginTypes = ref([{ text: 'Student Login', value: 'ldap' }, { text: 'Parent Login', value: 'local' }])

    return {
      tab,
      username,
      password,
      errorMessage,
      checkingLogin,
      userTemp,
      loginActive,
      buttonMessage,
      user,
      doLogin,
      checkSubmit,
      usernameField,
      passwordField,
      semiTransparent,
      timeout,
      resetTimeout,
      loginType,
      loginTypes
    }
  }
}
</script>
